import type { SortingState, Updater } from "@tanstack/react-table"
import { useCallback } from "react"
import type { RefObject } from "react"
import type { Client } from "urql"
import { useShallow } from "zustand/react/shallow"

import type { ProductInventoryPalletsSearchParams } from "../schemas"
import { useProductInventoryPalletsSearchStore } from "../stores/useProductInventoryPalletsSearchStore"

import type { InventoryByPalletItem } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"

const ROUTE = "/app/inventory/products/$productId/_layout/pallets"

interface UseProductInventoryPalletListParams {
  productId: number
}

interface UseProductInventoryPalletListReturn {
  inventories: InventoryByPalletItem[]
  isFetching: boolean
  error: Error | undefined
  sorting: SortingState
  searchParams: ProductInventoryPalletsSearchParams
  tableContainerRef: RefObject<HTMLDivElement>
  handleSortingChange: (updater: Updater<SortingState>) => void
  handleSearchChange: (value: string) => void
  refetch: () => void
  refetchLoaded: () => Promise<boolean>
  handleScroll: () => void
}

export const useProductInventoryPalletList = ({
  productId,
}: UseProductInventoryPalletListParams): UseProductInventoryPalletListReturn => {
  const store = useProductInventoryPalletsSearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, refetchLoaded, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      refetchLoaded,
      clearStore,
    }))
  )

  const executeSearch = useCallback(
    (client: Client, params: ProductInventoryPalletsSearchParams, skipFilterCheck?: boolean) => {
      store.executeSearch(client, { ...params, productId }, skipFilterCheck)
    },
    [store, productId]
  )

  const loadMore = useCallback(
    (client: Client, params: ProductInventoryPalletsSearchParams) => {
      store.loadMore(client, { ...params, productId })
    },
    [store, productId]
  )

  const refetchProductLoaded = useCallback(
    (client: Client, params: ProductInventoryPalletsSearchParams) => {
      return store.refetchLoaded(client, { ...params, productId })
    },
    [store, productId]
  )

  const {
    data: inventories,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    refetch,
    refetchLoaded,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
  } = useListPageBase<InventoryByPalletItem, ProductInventoryPalletsSearchParams>({
    route: ROUTE,
    store: { ...store, executeSearch, loadMore, refetchLoaded: refetchProductLoaded },
  })

  return {
    inventories,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    refetch,
    refetchLoaded,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
  }
}
